import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  Box,
  Grid,
  Link,
  Card,
  Modal,
  CardContent,
  CardHeader,
  IconButton,
  Checkbox,
} from "@mui/material";

import {
  useGetPurchaseOrderTemplateQuery,
  useUpdatePurchaseOrderDataMutation,
  useSavePurchaseOrderDraftMutation,
  useGetPurchaseOrderDraftQuery,
  useSavePurchaseOrderMutation,
  useUpdatePurchaseOrderDataActionMutation,
} from "../../features/po/poSlice";
import Loading from "../../components/Loading";
import DescriptionIcon from '@mui/icons-material/Description';
import { POHistoryReadOnlyView } from "../../components/POHistoryView";
import { useSelector } from "react-redux";
import { getUser } from "../../features/auth/authSlice";
import { capitalized, formatCurrency, contextFormat } from "../../helpers/strings";
import { POOptionItem, makeShortLabel } from "./Extras";
import { OrderSubmittedView } from "./OrderSubmittedView";
import { PurchaseOrderTableView } from "./PurchaseOrderTable";
import { PurchaseOrderCustomItemView } from "./PurchaseOrderCustomItems";
import { POPreviewView } from "./POPreviewView";
import {isMobile} from 'react-device-detect';
import { useDebounce } from "../../helpers/debounce";
import { PurchaseOrderContentField } from "./ContentField";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const makeSimpleTableHeader = (label) => {
  return (
    <Box display="flex" alignItems="center">
        <Typography variant="h2">{label}</Typography>
    </Box>
  );
};

function labelize(text) {
  return text.split("_").filter((p) => p !== "hidden").map((p) => capitalized(p)).join(" ");
}

const reconcileRowChanges = ({fields, changes, order_row, data}) => {
  var row_data = {...data};
  Object.keys(data).forEach((key) => {
    // is the field user-editable?
    if(fields[key]) {
      if(fields[key].type === "text" || fields[key].type === "choice") {
        // are there "future" entries in queue for this?
        if(changes) {
          if(changes.slice().filter((c) => c.key === key).length > 0) {
            let orig_value = order_row[key];
            row_data[key] = orig_value;
          }
        }
      }
    }
  });
  return row_data;
}

const reconcileChanges = ({sheets, changes, order, data}) => {
  // apply _data_ for changed rows onto _original_
  // unless changes have updates to user-editable fields, in which case pick those
  var new_data = {...data};
  changes.forEach((change) => {
    if(change.sheet === "____") {
      new_data = reconcileRowChanges({fields: sheets, changes: changes.filter((c) => c.sheet === change.sheet && c.key === change.key), order_row: {...order}, data: new_data});
    } else {
      let row_data = data[change.sheet][change.row];
      if(row_data) {
        var new_rows = [...new_data[change.sheet]];
        var order_rows = [...order[change.sheet]];
        let order_row = order_rows[change.row];
        let fields = Object.fromEntries(sheets[change.sheet].fields.map((field) => [field.key, field]));
        new_rows[change.row] = reconcileRowChanges({fields: fields, changes: changes.filter((c) => c.sheet === change.sheet && c.row === change.row), order_row: order_row, data: row_data})
        new_data[change.sheet] = new_rows;
      }
    }
  });
  return new_data;
}

const PurchaseOrderForm = ({orderStatusInfo=null, readOnly=false, hideHistory=false, showSums=false}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = useState({});
  const [draftLoad, setDraftLoad] = useState(false);
  const [tableOpenStates, setTableOpenStates] = useState({});
  const [errorPopup, setErrorPopup] = useState("");
  const [stickyElements, setStickyElements] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [preview, setPreview] = useState({table: "", row_key: -1, enabled: false});
  const {data: formData, error, isLoading} = useGetPurchaseOrderTemplateQuery({draft_id: searchParams.get("draft_id") || null, type: searchParams.get("type") || "regular custom"});
  const [updateFormActionData, { isLoading: nopeisRowUpdating, data: thePartialFormTotalsData }] = useUpdatePurchaseOrderDataActionMutation();
  const [changedRows, setChangedRows] = useState([]);
  const [changesToReconcile, setChangesToReconcile] = useState(null);
  const [updateModal, setUpdateModal] = useState("");
  const debouncedChanges = useDebounce(changedRows, 600);
  const [saveDraft, { data: draftData, isLoading: savingDraft }] = useSavePurchaseOrderDraftMutation();
  const [submitOrder, { data: orderData, isLoading: submittingOrder, isSuccess: orderSubmitted }] = useSavePurchaseOrderMutation();
  const user = useSelector(getUser);
  const { data: draftPo } = useGetPurchaseOrderDraftQuery(
    searchParams.get("draft_id"),
    { skip: !searchParams.get("draft_id") }
  );

  const isUpdating = false;

  useEffect(() => {
    if(!formData || !order) return;
    if(!changesToReconcile) return;
    let sheets = Object.fromEntries(formData.form_contents.map((content) => [content.key, content]));
    setOrder(reconcileChanges({sheets: sheets, changes: changedRows, order: order, data: changesToReconcile}));
    setChangesToReconcile(null);
  }, [changesToReconcile, setChangesToReconcile, order, changedRows, formData]);

  useEffect(() => {
    const asyncDebounce = async (debouncedChanges) => {
      if(debouncedChanges.length > 0) {
        let changes = {};
        let changed_rows = [...debouncedChanges];
        debouncedChanges.forEach((r) => {
          if(order[r.sheet])
            changes[r.sheet] = [...order[r.sheet]];
        })
        setChangedRows([]);
        await updateFormActionData({data: {...order, ...changes, _rows: changed_rows}, action: "rows"}).then((data) => {
          if(data.data) {
            setChangesToReconcile(data.data);
            setUpdateModal("");
          }
        })
      } else {
        console.log("No change")
      }
    }
    if (debouncedChanges) {
      if (debouncedChanges.length > 0) {
        asyncDebounce(debouncedChanges);
      }
    }
  }, [debouncedChanges, updateFormActionData]);

  const admin = user?.permissions.includes("admin") || false;
  useEffect(() => {
    if (formData && stickyElements.length === 0) {
      if (formData.form_contents) {
        const stickyElements = formData.form_contents.filter(
          (e) => e.sticky === true
        );
        setStickyElements(stickyElements);
        console.log("Setting sticky elements");
      }
    }
    if (!isLoading && draftPo && draftLoad === false) {
      console.log("Loading the draft");
      setDraftLoad(true);
      setOrder((order) => ({ ...order, ...draftPo }));
      const tables = formData?.form_contents.filter((e) => e.type === "table") ?? [];
    }
  }, [isLoading, draftPo, order, draftLoad, formData]);

  useEffect(() => {
    if (draftData && draftData.draft_id && order.draft_id === undefined) {
      console.log("Updating order data");
      setOrder((previous) => ({ ...previous, draft_id: draftData.draft_id }));
      // set the URL
      setSearchParams(new URLSearchParams({ draft_id: draftData.draft_id }));
    }
  }, [draftData, order, setSearchParams]);

  if (isLoading) {
    return <Loading />;
  }
  
  if (orderSubmitted) {
    return <OrderSubmittedView link={window.location.href.split("?")[0] + "?draft_id=" + orderData.draft_id} />
  }

  const handleTableChange = ({tableKey, rowKey, fieldKey, value, applyToAll=false}) => {
    let oldTable = [...(order[tableKey] || [])];
    var oldRow = { ...(oldTable[rowKey] || {}) };
    if(value === null) {
      oldRow[fieldKey] = "";
      delete oldRow[fieldKey];
    } else {
      oldRow[fieldKey] = value;
    }
    oldTable.splice(rowKey, 1, oldRow);

    let changeds = [{sheet:tableKey, row:rowKey, key: fieldKey, value: value}];
    let newOldTable = [...oldTable];
    if(applyToAll) {
      newOldTable.forEach((row, idx) => {
        if(row?.[fieldKey] || null !== value) {
          oldTable.splice(idx, 1, {...row, [fieldKey]: value});
          changeds.push({sheet:tableKey, row: idx, key: fieldKey, value: value})
        }
      });
      if(oldTable.length > 5) {
        setUpdateModal(`Applying the value to all ${oldTable.length} items. Please wait without closing the page.`);
      }
    }
    // add to changes. include any automatic rows if there are
    newOldTable.forEach((row, idx) => {
      if(row?.type === "Parts" && row?.parts === "Toe Kick Totaled For Job"){
        changeds.push({sheet: tableKey, row: idx});
        // is the item flagged?
        if(!(row?._automatic || false)) {
          oldTable.splice(idx, 1, {...row, _automatic: "Toe Kick / Crown Molding"})
        }
      } else {
        // is the item flagged?
        if((row?._automatic || false)) {
          let newRow = {...row};
          delete newRow["_automatic"];
          oldTable.splice(idx, 1, newRow);
          changeds.push({sheet: tableKey, row: idx});
        }
      }
    })
    setChangedRows([...changedRows, ...changeds])

    setOrder((prev) => ({
      ...prev,
      [tableKey]: oldTable
    }))
  };


  const renderTextInput = (element, index) => {
    return (
      <TextField
        key={index}
        InputProps={{
          readOnly: element.readonly || readOnly
        }}
        label={element.label}
        value={order[element.key] || ""}
        error={validationErrors?.[element.key]}
        onChange={(e) => {
          setOrder({ ...order, [element.key]: e.target.value });
          setChangedRows([...changedRows, {sheet: "____", row: 0, key: element.key}]);
        }}
        sx={{ minWidth: "300px", maxWidth: "360px" }}
      />
    );
  };

  const renderCheckbox = (element, index) => {
    return <Stack direction="row" sx={{alignItems: "center"}}>
      <Checkbox checked={order[element.key]} onChange={(e) => {
        setOrder({ ...order, [element.key]: e.target.checked});
        setChangedRows([...changedRows, {sheet: "____", row:0, key: element.key}]);
      }}/>
      {
        element.link ?
          <Link target="_blank" href={element.link}>{element.label}</Link>
        :
          <Typography variant={(element.style ?? '').includes('small') ? "p" : "h2"}>{element.label}</Typography>
      }
    </Stack>;
  }

  const renderButton = (element, index) => {
    const disabled = element.depend ? (!admin && (!order[element.depend] ?? true)): false;
    return (
      <Button
        key={index}
        sx={{ maxWidth: "300px" }}
        variant={element.key === "submit_button" ? "contained" : "outlined"}
        disabled={disabled}
        color="primary"
        onClick={handleButtonClick}
      >
        {element.label}
      </Button>
    );
  };

  const renderStickyElement = (content, index) => {
    if (content.type === "text") {
      return (
        <Grid item key={index}>
          {renderTextInput(content)}
        </Grid>
      );
    } else if (content.type === "title") {
      return (
        <Grid item key={index} xs={12}>
          <Typography variant="h2">{content.label}</Typography>
        </Grid>
      );
    } else if (content.type === "error") {
      return (
        <Grid item key={index} xs={12}>
          <Stack direction="column">
            {
              (order?.[content.key] || "").split("\n").map((row, idx) => 
                <Typography variant="caption" sx={{ color: "red" }} key={`row_${idx}`}>
                  {row}
                </Typography>
              )
            }
          </Stack>
        </Grid>
      );
    } else if (content.type === "total") {
      return (
        <Grid item key={index} xs={12}>
          <Typography variant="h3">
            {content.label}:{" "}
            {isUpdating
              ? "Calculating..."
              : formatCurrency(
                order?.[content.key] || "0.0"
                )}
          </Typography>
        </Grid>
      );
    } else if (content.type === "button") {
      if (!(content.secondary || false) || admin) {
        return (
          <Grid item key={index}>
            {renderButton(content)}
          </Grid>
        )
      }
      if (content.secondary || false) {
        return <Grid item key={index}>
            <Button variant="text">{content.secondary_label || ""}</Button>
          </Grid>
      }
    } else if (content.type === "boolean") {
      let extras = {};
      if ((content.style ?? '').includes('row')) {
        extras['xs'] = 12;
      }

      return <Grid item key={index} {...extras}>
          {renderCheckbox(content, index)}
        </Grid>
    }
    return null;
  }

  const handleButtonClick = (e) => {
    if (e.target.textContent === "Save Draft" || e.target.textContent === "Update Draft") {
      handleSaveDraft();
    } else if (e.target.textContent === "Submit" || e.target.textContent === "Update") {
      handleSubmit();
    }
  };

  const validateEmail = (email) => {
    return email.match(
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/
    );
  };

  const handleSaveDraft = () => {
    if (
      !order["email_address"] ||
      order["email_address"].toString().trim() === ""
    ) {
      console.log("email address required");
      setValidationErrors((prev) => ({
        ...prev,
        email_address: true,
      }));
      setErrorPopup("Email address is required.")
    } else {
      if(!validateEmail(order["email_address"])) {
        setValidationErrors((prev) => ({
        ...prev,
        email_address: true,
      }));
      setErrorPopup(`Invalid email address: ${order['email_address']}`)
      } else {
        setValidationErrors(Object.fromEntries(Object.entries(validationErrors).filter((e) => !e[0] === 'email_address')))
        saveDraft(order);
      }
    }
  };

  const handleSubmit = async () => {
    let isValid = true;
    let validationErrors = {};
    formData.form_contents.forEach((content) => {
      if (content.type === "table") {
        content.fields.forEach((field) => {
          if (
            !field.nullable &&
            field.type !== "auto" &&
            field.type !== "total" &&
            field.type !== "variable"
          ) {
            if((field.conditional) && (field.conditional !== "")) {
              return;
            }
            const tableOrder = order[content.key] || [];
            tableOrder.forEach((row, index) => {
              var setInvalid = false;
              if (
                row &&
                (!row[field.key] || row[field.key].toString().trim() === "")
              ) {
                setInvalid = true;
              }
              if (row && (field.validation || "" == "range")) {
                let current_value = parseFloat(row[field.key] || "0.0")
                if (((field.max_value || 0) !== 0)) {
                  if (current_value > field.max_value) {
                    setInvalid = true;
                  }
                }
                if (((field.min_value || 0) !== 0)) {
                  if (current_value < field.min_value) {
                    setInvalid = true;
                  }
                }
              }
              if (setInvalid) {
                isValid = false
                if (!validationErrors[content.key]) {
                  validationErrors[content.key] = [];
                }
                validationErrors[content.key][index] = [
                  ...(validationErrors[content.key][index] ?? []),
                  field.key,
                ];
              }
            });
          }
        });
      } else if (content.type === "text" && !content.nullable) {
        if (
          !order[content.key] ||
          order[content.key].toString().trim() === ""
        ) {
          isValid = false;
          validationErrors[content.key] = "This field is required";
          if (content.category === "shipping") {
            setTableOpenStates({...tableOpenStates, "Contact information": true})
          }  
        }
      }
    });

    if (isValid) {
      // TODO null rows may be present, we should filter out before submitting
      setValidationErrors({});
      submitOrder(order);
    } else {
      console.log("Form is invalid:", validationErrors);
      setValidationErrors(validationErrors);
      setErrorPopup("Please check each row in your order, and your contact information, and fill in any missing info. Thank You");
    }
  };

  if (error) {
    return <div>{error.error}</div>;
  }
  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (!formData.form_contents) {
    return <div>Loading...</div>;
  }

  let draft_download_link = searchParams.get("draft_id") ? <Grid item key="download_pdf">
    <Button component={Link} variant="outlined" target="_blank" href={`${process.env.REACT_APP_API_URL}v1/po/pdf/${searchParams.get("draft_id")}`}><DescriptionIcon sx={{marginRight: 1}} /> Download as PDF</Button>
  </Grid> : <></>;

  let packing_download_link = searchParams.get("draft_id") ? <Grid item key="packing_pdf">
  <Button component={Link} variant="outlined" target="_blank" href={`${process.env.REACT_APP_API_URL}v1/po/packingpdf/${searchParams.get("draft_id")}`}><DescriptionIcon sx={{marginRight: 1}} /> Packing list</Button>
  </Grid> : <></>;

  function shouldShowTable(key) {
    if(key.startsWith("custom_")) {
      // only if there are contents
      if(key in order) {
        if(order[key].length > 0) {
          return true;
        }
      }
      return false;
    }
    if (!readOnly) {
      return true;
    }
    if (order?.[key] === undefined) {
      return false;
    }
    return order?.[key].filter((a => a !== null)).length > 0;
  }

  function conditionaled(form, data) {
    var ret = {};
    for(var key in data) {
      // todo: hide conditional hidden values
    }
    return data;
  }

  function createPreviewData() {
    return {
      table: formData[preview.table],
      row: {...order[preview.table][preview.row], ...conditionaled(formData[preview.table], order[preview.table][preview.row])}
    }
  }

  function onAddCustomItem(item, meta) {
    const sheet = meta["custom_sheet"].svalue;
    // create the row
    const newRow = {
      custom_code: item.code
    };
    Object.keys(meta).forEach((key) => {
      if(key !== "custom_sheet") {
        newRow[key] = meta[key].svalue;
      }
    });

    // open that sheet
    setTableOpenStates({...tableOpenStates, [sheet]: true});
    var current = {...order};
    if(!(sheet in current)) {
      setOrder({...current, [sheet]: [newRow]});
    } else {
      var rows = [...current[sheet]];
      rows.push(newRow);
      setOrder({...current, [sheet]: rows});
    }
  }


  const handleRowChange = ({table_key, change, row_key, data=[], custom=null}) => {
    let newTable = [...order?.[table_key] || []];
    if(change === "add_row") {
      let auto_idx = 0
      newTable.forEach((row, idx) => {
          if(row._automatic) {
            if(auto_idx === 0) auto_idx = idx;
          }
      });
      if(auto_idx) {
        newTable.splice(auto_idx, 0, {});
      } else {
        newTable.push({});
      }
      setOrder({...order, [table_key]:newTable});
      setChangedRows([...changedRows, {sheet: table_key, row: row_key}])
    } else if(change === "add_rows") {
      let row_idx = newTable.length;
      newTable.push(...data);
      setOrder({...order, [table_key]:newTable});
      let changes = [];
      data.forEach((d) => {
        changes.push({sheet: table_key, row: row_idx});
        row_idx += 1;
      })
      setChangedRows([...changedRows, ...changes]);
    } else if(change === "duplicate_row") {
      let duplicateRow = { ...newTable[row_key] };
      if (duplicateRow.cabinet_number) {
        let curnum = parseInt(duplicateRow.cabinet_number);
        if (curnum) {
          duplicateRow.cabinet_number = `${curnum+1}`;
        }
      }
      newTable.splice(row_key+1, 0, duplicateRow);
      setOrder({...order, [table_key]:newTable});
      setChangedRows([...changedRows, {sheet: table_key, row: row_key+1}])
    } else if(change === "delete_row") {
      let newTable = [...order?.[table_key] || []];
      newTable.splice(row_key, 1);
      if(!custom) {
          if (newTable.length === 0) {
              //newTable.push({});
          }
      }
      setOrder({...order, [table_key]:newTable});
      setChangedRows([...changedRows, {sheet: table_key, row: row_key}])
    }
  };

  let modal = (submittingOrder || savingDraft) ? <Modal key="savemodal"
      open={true}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description">
        <center>
          <Card  sx={{ width: 200 }}>
        <h2 id="parent-modal-title">Please wait</h2>
        <p id="parent-modal-description">
          The form is being submitted.
        </p>
      </Card>
      </center>
    </Modal>
    : errorPopup !== "" ? <Modal key="infomodal"
      open={true}
      sx={{marginTop: 4}}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description">
        <center>
          <Card  sx={{ width: 400 }}>
        <h2 id="parent-modal-title">Missing info</h2>
        <p id="parent-modal-description">
          {errorPopup}
        </p>
        <Button sx={{marginBottom: 2}} onClick={() => {setErrorPopup("");}} variant="contained">Ok</Button>
        
      </Card>
      </center>
    </Modal> : preview.enabled ?
    <Modal key="previewModal"
      open={true}
      sx={{marginTop: 4}}
      aria-labelledby="parent-modal-title">
      <center>
        <Card sx={{ width: 600 }}>
        <h2 id="parent-modal-title">Preview</h2>
          <POPreviewView data={createPreviewData()} />
          <Button onClick={()=>{
            setPreview({...preview, enabled: false});
            console.log("Closing...")
            }}>Close</Button>
        </Card>
      </center>
      </Modal> :
    updateModal !== "" ? <Modal key="updateModal"
      open={true}
      sx={{marginTop: 4}}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description">
        <center>
          <Card  sx={{ width: 400, paddingLeft: "20px", paddingRight: "20px" }}>
        <h2 id="parent-modal-title">Updating, please wait.</h2>
        <p id="parent-modal-description">
          {updateModal}
        </p>
      </Card>
      </center>
    </Modal> :
    <></>;

  let history = hideHistory ? <></> : (orderStatusInfo !== null ? <Card><POHistoryReadOnlyView order={orderStatusInfo} /></Card> : <></>);
  let summeds = <></>;
  if(showSums && order) {
    if(order._summed) {
      summeds = <Card variant="outlined" key="summed">
        <CardHeader title="Summed production totals" />
        <CardContent>
          {Object.keys(order._summed).sort().map((key) => <Typography key={`sum_${key}`}>{labelize(key)}: {contextFormat(order._summed[key], key)}</Typography>)}
          </CardContent>
      </Card>;
    }
  }

  let shownStickyElements = stickyElements.filter((e) => {
    if(e.key) {
      if(e.key === "discounted_total" || e.key === "discounted_subtotal") {
        if ((order?.["discounted_total"] | 0.0) === 0.0) {
          return false;
        }
      }
    }
    if(order?.["show_shipping"] || false) {
      return true;
    }
    if(e.key) {
      if(e.key.includes("shipping")) return false;
    }
    return true;
  });
  
  return (
    <>
      <AppBar
        position={isMobile ? "relative" : "sticky"}
        color="inherit"
        variant="outlined"
        elevation={0}
      >
        <Toolbar sx={{ padding: 2 }}>
          <Grid container columnSpacing={2} rowSpacing={2}>
            {shownStickyElements.map((content, index) => {
              return renderStickyElement(content, index);
            })}
            {
              draft_download_link
            }
            {
              packing_download_link
            }
            <Grid item key="pricelist" sx={{visibility: "hidden"}}>
              <Button
                component={Link}
                variant="outlined"
                target="_blank"
                href={
                  process.env.REACT_APP_API_URL +
                  "/v1/po/pricelist/" +
                  (order?.["customer_code"] || "")
                }
              >
                Download price list
              </Button>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {history}
      {summeds}
      <Paper variant="outlined" sx={{ padding: 2, backgroundColor: "#f7f7f7" }} >
      <Stack direction="column" spacing={2} padding={isMobile ? 0 : 2}>
        {makeSimpleTableHeader("Contact Information")}
        {formData && formData.form_contents
          .filter((c) => {
            return (c.category || "") === "shipping";
          })
          .map((content, index) => {
          return <PurchaseOrderContentField
            key={`${index}_${content.key}`}
            field={content}
            elementWidth={300}
            controlSize="small"
            readOnly={readOnly}
            onClick={handleButtonClick}
            hasErrors={validationErrors?.[content.key]}
            options={content.type === "choice" ? formData.definitions[content.options] : []}
            current_value={order[content.key] || ""}
            onChange={(value) => {
              setOrder({ ...order, [content.key]: value });
              setChangedRows([...changedRows, {sheet: "____", row: 0, key: content.key}]);
            }}
          />})
        }
      </Stack>
      </Paper>
      <Stack direction="column" spacing={2} padding={isMobile ? 0 : 2}>
        {formData.form_contents
          .filter((c) => {
            return !(c.sticky || false);
          })
          .filter((c) => {
            return (c.category || "") !== "shipping";
          })
          .map((content, index) => {
            if(content.type === "table") {
              if(shouldShowTable(content.key)) {
                return <PurchaseOrderTableView
                  key={content.key}
                  table={content}
                  order={order}
                  isAdmin={admin}
                  handleRowChange={handleRowChange}
                  readOnly={readOnly}
                  handleTableChange={handleTableChange} 
                  tableOpenStates={tableOpenStates}
                  setTableOpenStates={setTableOpenStates}
                  validationErrors={validationErrors?.[content.key]}
                  formData={formData}
                />;
              } else {
                return <></>;
              }
            }
            return <PurchaseOrderContentField
              key={content.key}
              field={content}
              elementWidth={300}
              controlSize="small"
              readOnly={readOnly}
              onClick={handleButtonClick}
              hasErrors={validationErrors?.[content.key]}
              options={content.type === "choice" ? formData.definitions[content.options] : []}
              current_value={order?.[content.key] || ""}
              onChange={(value) => {
                setOrder({ ...order, [content.key]: value })
              }}
            />
          })
        }
        <PurchaseOrderCustomItemView onAddCustomItem={onAddCustomItem} />
      </Stack>
      <Box height={200} alignContent="end"></Box>
      {modal}
    </>
  );
}

export default PurchaseOrderForm;
